import React, { useEffect, useRef, useState } from 'react'

import { List, ListItem, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'

import { useRecoilValue } from 'recoil'
import { useChatData } from 'components/contexts/ChatContext'
import { CHAT_AVATAR_TEXT_COLOR, createMessageReadMsg, getUserNameInitials } from 'utils/chatFunctions'
import { isoStringToLocaleStr } from 'utils/dateTimeFunctions'
import { userRoleSelector } from 'atoms'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const sortDatetime = (a, b) => {
  if (typeof a.date !== 'string') return -1
  if (typeof b.date !== 'string') return -1
  let d1 = new Date(Date.parse(a.timeStamp))
  let d2 = new Date(Date.parse(b.timeStamp))
  if (d1 < d2) return -1
  if (d1 > d2) return 1

  return 0
}

const StyledOwnRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  width: '100%',
  alignItems: 'center',
})

const StyledOtherRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
})

const ConversationWindow = () => {
  const scrollRef = useRef(null)
  const _isMounted = useRef(false)
  const { chat } = useChatData()
  const selectedChatRoom = chat.selectedChatRoom
  const newMessages = chat.unreadMessages
  const ownMessage = chat.ownMessage
  const [chatData, setChatData] = useState([])
  const { user } = useInnovaAuth()
  const userRole = useRecoilValue(userRoleSelector)
  const { sendMessage } = chat
  const { getBackColor, getGrayColor, theme } = useInnovaTheme()
  const ownBubbleColors =
    theme === 'dark'
      ? { backgroundColor: '#244b68', borderColor: '#315f80', color: '#f5f9fc' }
      : { backgroundColor: '#d7e7f5', borderColor: '#bfd6e9', color: '#16324f' }
  const otherBubbleColors =
    theme === 'dark'
      ? { backgroundColor: '#30363b', borderColor: '#41484e', color: '#f3f4f6' }
      : { backgroundColor: '#f0f2f4', borderColor: '#dde1e5', color: '#202428' }

  useEffect(() => {
    _isMounted.current = true

    return () => {
      _isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (_isMounted.current && selectedChatRoom) {
      getData()
    }
  }, [selectedChatRoom]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (_isMounted.current && newMessages.chatMsgs?.length > 0) {
      let newMsgs = []
      newMessages.chatMsgs.forEach((newMsg) => {
        if (newMsg.roomId === selectedChatRoom.roomId) {
          newMsgs.push(newMsg)
        }
      })

      newMsgs.sort(sortDatetime)

      //mark these new messages as read
      newMsgs.forEach((msg) => {
        let msgRead = createMessageReadMsg(msg, userRole?.organization)
        if (msgRead !== null) {
          sendMessage(msgRead)
        }
      })

      // check to see if message is already in the list
      let uniqueMsgs = []
      newMsgs.forEach((msg) => {
        let index = chatData.findIndex((element) => element.messageId === msg.messageId)
        if (index < 0) uniqueMsgs.push(msg)
      })

      const allMsgs = [...chatData, ...uniqueMsgs] // newMessages.chatMsgs[newMessages.chatMsgs.length-1]]
      setChatData(allMsgs)
    }
  }, [newMessages]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (_isMounted.current && ownMessage) {
      let messages = [...chatData]
      messages.push(ownMessage)
      setChatData(messages)
    }
  }, [ownMessage]) // eslint-disable-line react-hooks/exhaustive-deps

  const getData = async () => {
    if (!selectedChatRoom || !selectedChatRoom?.roomId || selectedChatRoom?.roomId < 0) {
      return
    }
    const messages = await chat.fetchChatMessages(selectedChatRoom.roomId)
    if (messages.success === false) {
      setChatData([])
      return
    }

    if (messages.data.length > 0) {
      let sortedData = [...messages.data]
      sortedData.sort(sortDatetime)

      await chat.markChatMessagesAsRead(sortedData)

      setChatData(sortedData)
    } else {
      setChatData([])
    }
  }

  const renderOwnMessage = (item, index) => {
    let msg = item.message.replace('\\\\', '\\')
    msg = msg.replace('&sq;', "'")
    return (
      <StyledOwnRow>
        <Box
          sx={{
            display: 'flex',
            padding: '12px 12px 6px 12px',
            borderRadius: '10px',
            border: '1px solid',
            ...ownBubbleColors,
            marginLeft: '20%',
            whiteSpace: 'normal',
            maxWidth: '75%',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
            <Box sx={{ fontSize: '16px' }}>{msg}</Box>
            <StyledOwnRow>
              <Box sx={{ padding: '8px 0px 0px 0px', fontSize: '10px', textAlign: 'right' }}>
                {isoStringToLocaleStr(
                  item.timeStamp.length > 1 ? item.timeStamp : new Date(Date.now()).toISOString(),
                  false,
                  true,
                  false,
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '12px',
                  fontWeight: 700,
                  margin: '8px 0px 0px 8px',
                  backgroundColor: theme === 'dark' ? 'lightgrey' : '#fff',
                  color: CHAT_AVATAR_TEXT_COLOR,
                  border: `2px solid ${theme === 'dark' ? 'lightgrey' : '#d4d9de'}`,
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                }}>
                {getUserNameInitials(item.username).toUpperCase()}
              </Box>
            </StyledOwnRow>
          </Box>
        </Box>
      </StyledOwnRow>
    )
  }

  const renderOtherMessage = (item, index) => {
    let msg = item.message.replace('\\\\', '\\')
    msg = msg.replace('&sq;', "'")
    return (
      <StyledOtherRow>
        <Box
          sx={{
            display: 'flex',
            padding: '12px',
            borderRadius: '10px',
            border: '1px solid',
            ...otherBubbleColors,
            marginLeft: '8px',
            marginRight: '20%',
            whiteSpace: 'normal',
            maxWidth: '75%',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
            <Box sx={{ fontSize: '16px' }}>{msg}</Box>
            <StyledOtherRow>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '12px',
                  fontWeight: 700,
                  margin: '8px 8px 0px 0px',
                  backgroundColor: theme === 'dark' ? 'lightgrey' : '#fff',
                  color: CHAT_AVATAR_TEXT_COLOR,
                  border: `2px solid ${theme === 'dark' ? 'lightgrey' : '#d4d9de'}`,
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                }}>
                {getUserNameInitials(item.username).toUpperCase()}
              </Box>
              <Box sx={{ padding: '8px 0px 0px 0px', fontSize: '10px', textAlign: 'right' }}>
                {isoStringToLocaleStr(item.timeStamp, false, true, false)}
              </Box>
            </StyledOtherRow>
          </Box>
        </Box>
      </StyledOtherRow>
    )
  }

  const renderChatMessage = (item, index) => {
    if (item.username.toLowerCase() === user.name?.toLowerCase()) {
      return renderOwnMessage(item, index)
    } else {
      return renderOtherMessage(item, index)
    }
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behaviour: 'smooth' })
    }
  }, [chatData])

  return (
    <Box
      sx={{
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      <List
        dense
        sx={{
          width: '100%',
          height: '100%',
          background: getBackColor(),
          overflow: 'auto',
          position: 'relative',
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
            WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: getGrayColor(0.25),
            outline: `1px solid ${getGrayColor(0.12)}`,
          },
        }}>
        {chatData?.map((item, index) => (
          <ListItem
            sx={{
              display: 'flex',
              padding: '4px 0px 4px 0px',
              borderRadius: '5px',
              width: 'calc(100% - 10px)', // 8px for marginLeft, 2px for left & right border
            }}
            dense
            disableGutters={true}
            key={`${index}`}
            ref={index === chatData.length - 1 ? scrollRef : null}>
            {renderChatMessage(item, index)}
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

export default ConversationWindow
